import * as Dialog from "@radix-ui/react-dialog";
import { clsx } from "clsx";
import { CalendarDays, Facebook, MapPin, Menu, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useSalon } from "@/hooks/useSalon";
import { site } from "@/content";
import { hoursLabel, WEEK } from "@/lib/format";
import { Logo } from "./Logo";
import { Container, LinkButton, PoleBar } from "./ui";

const nav = [
  { to: "/services", label: "Services" },
  { to: "/team", label: "Team" },
  { to: "/reviews", label: "Reviews" },
  { to: "/visit", label: "Hours & Visit" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={clsx("sticky top-0 z-40 transition", scrolled ? "bg-cream/90 shadow-soft backdrop-blur" : "bg-cream")}>
      <PoleBar />
      <Container className="flex h-18 items-center justify-between gap-4 py-3">
        <Link to="/" className="shrink-0" aria-label="Style & Curl home">
          <Logo size="sm" />
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                clsx("rounded-full px-4 py-2 text-sm font-semibold transition", isActive ? "text-rust" : "text-ink hover:text-rust")
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a href={`tel:${site.phone}`} className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-ink hover:text-rust lg:flex">
            <Phone className="size-4" aria-hidden /> {site.phoneDisplay}
          </a>
          <LinkButton to="/book" size="sm" className="hidden sm:inline-flex">
            Book now
          </LinkButton>
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger className="grid size-11 place-items-center rounded-full text-ink hover:bg-sand md:hidden" aria-label="Open menu">
              <Menu className="size-6" />
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
              <Dialog.Content className="fixed inset-y-0 right-0 z-50 flex w-[85%] max-w-sm animate-fade-up flex-col bg-cream p-6 shadow-lift">
                <div className="flex items-center justify-between">
                  <Dialog.Title asChild>
                    <span>
                      <Logo size="sm" />
                    </span>
                  </Dialog.Title>
                  <Dialog.Close className="grid size-11 place-items-center rounded-full hover:bg-sand" aria-label="Close menu">
                    <X className="size-6" />
                  </Dialog.Close>
                </div>
                <Dialog.Description className="sr-only">Site navigation</Dialog.Description>
                <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
                  {[{ to: "/", label: "Home" }, ...nav].map((n) => (
                    <NavLink key={n.to} to={n.to} end className={({ isActive }) => clsx("rounded-2xl px-4 py-3 font-display text-2xl", isActive ? "bg-rust-50 text-rust" : "text-ink")}>
                      {n.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-auto grid gap-3">
                  <LinkButton to="/book" size="lg">
                    <CalendarDays className="size-5" aria-hidden /> Book now
                  </LinkButton>
                  <a href={`tel:${site.phone}`} className="text-center text-sm font-semibold text-muted">
                    or call {site.phoneDisplay}
                  </a>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </Container>
    </header>
  );
}

function Footer() {
  const { data } = useSalon();
  const hours = data?.salon.hours;
  return (
    <footer className="mt-24 bg-ink text-white/80">
      <PoleBar />
      <Container className="grid gap-12 py-16 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <span className="font-script text-5xl text-white">Style &amp; Curl</span>
          <p className="mt-4 max-w-sm">
            Anna's hometown salon and barber shop since {site.established}. Cuts, color, curls and classic sets for the whole family.
          </p>
          <div className="mt-6 flex gap-3">
            <a href={site.facebookUrl} target="_blank" rel="noreferrer" className="grid size-11 place-items-center rounded-full bg-white/10 hover:bg-white/20" aria-label="Style & Curl on Facebook">
              <Facebook className="size-5" />
            </a>
          </div>
        </div>
        <div>
          <p className="eyebrow text-white/60">Visit</p>
          <a href={site.mapsUrl} target="_blank" rel="noreferrer" className="mt-4 flex gap-2 hover:text-white">
            <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              {site.address.line1}
              <br />
              {site.address.city}, {site.address.state} {site.address.zip}
            </span>
          </a>
          <a href={`tel:${site.phone}`} className="mt-3 flex items-center gap-2 hover:text-white">
            <Phone className="size-4" aria-hidden /> {site.phoneDisplay}
          </a>
        </div>
        <div>
          <p className="eyebrow text-white/60">Hours</p>
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {WEEK.map((d) => (
              <div key={d.key} className="contents">
                <dt className="text-white/60">{d.short}</dt>
                <dd>{hours ? hoursLabel(hours, d.key) : "…"}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Container>
      <div className="border-t border-white/10">
        <Container className="flex flex-col items-start justify-between gap-2 py-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Style &amp; Curl · {site.tagline}</p>
          <Link to="/staff" className="hover:text-white">
            Staff sign in
          </Link>
        </Container>
      </div>
    </footer>
  );
}

function MobileActionBar() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-cream/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:hidden">
      <div className="grid grid-cols-[auto_1fr] gap-3">
        <a href={`tel:${site.phone}`} className="grid size-12 place-items-center rounded-full bg-white ring-1 ring-line" aria-label={`Call ${site.phoneDisplay}`}>
          <Phone className="size-5 text-rust" />
        </a>
        <LinkButton to="/book" size="lg" className="h-12">
          <CalendarDays className="size-5" aria-hidden /> Book an appointment
        </LinkButton>
      </div>
    </div>
  );
}

export function SiteLayout() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo({ top: 0 }), [pathname]);
  return (
    <div className="flex min-h-dvh flex-col pb-20 sm:pb-0">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-full focus:bg-white focus:px-4 focus:py-2">
        Skip to content
      </a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <MobileActionBar />
    </div>
  );
}
